import { useRef } from "react";
import { NavLink } from "react-router-dom";
import {
  Ban,
  Building2,
  ChevronsLeft,
  ChevronsRight,
  CircleDollarSign,
  FileText,
  Folder,
  HardHat,
  Headset,
  Landmark,
  LayoutGrid,
  Library,
  Notebook,
  ScrollText,
  ShieldAlert,
  ShieldCheck,
  Store,
  Tag,
  Users,
  type LucideIcon,
} from "lucide-react";
import { hasCapability, type BrokerIdentity } from "@/lib/brokerAuth";
import { useProductLine } from "@/platform/ProductLineProvider";
import { PRODUCT_LINE_NAV } from "@/platform/productLine";
import { useSupportChat } from "@/components/SupportChatProvider";
import { ListScrollCue } from "@/components/ScrollCue";
import { ProductLineSwitcher } from "@/components/ProductLineSwitcher";
import { Brand } from "@/components/Brand";

// The left-nav contents: the primary destinations. Rendered once by PortalShell
// — as the persistent sidebar at lg+ and inside the off-canvas drawer below lg —
// so there's a single source of the nav (DRY). The destination list is driven by
// the active platform (Commercial | Surety) via PRODUCT_LINE_NAV, so switching
// the product line reskins the nav in place. Real destinations are gated off
// capabilities, never the raw role string. Every row WITHOUT a route — the shared
// items (Documents, Commissions, Support) and the destinations a platform
// hasn't wired yet — opens live chat when it is configured, so a dead control
// becomes "nothing here yet, ask us" (BRK-S136).
//
// The look is Nav's Claude Design sidebar (BRK-S258), in the brk tokens: the
// Signal logo in the top block; outline icons on rounded
// rows, the current one on the accent pill; Support set apart at the bottom,
// the portal's one support entry. The broker's identity stays in the top bar
// (BRK-S242). At lg+ a round control on the sidebar's border folds it to an
// icon rail (BRK-S244) that keeps the Signal mark on top: every collapsed style
// is lg-prefixed, so the phone drawer always renders in full. Below sm the open
// drawer also carries the product-line pill, which the header has no room for
// on a phone (BRK-S248).
interface SideNavProps {
  identity: BrokerIdentity | null;
  /** Called when a destination is chosen — lets the mobile drawer close itself. */
  onNavigate?: () => void;
  /** At lg+, the sidebar is an icon rail (BRK-S244). */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  /** The drawer is open: below sm, show the product-line pill at its top
   *  (BRK-S248). Rendered only then, so a closed drawer or the desktop rail
   *  never holds a second, hidden copy. */
  platformSwitcher?: boolean;
}

/** The sidebar element's id: the targets of the header hamburger's and the
 *  collapse control's aria-controls. */
export const SIDEBAR_ID = "portal-sidenav";

/** A row: 40px, with a 14px label and Nav's 22px icon (BRK-S262), and a
 *  44px target under a coarse pointer (BRK-S264). */
const linkBase =
  "group flex h-10 shrink-0 items-center gap-3 rounded-lg px-3 text-body transition-colors [@media(pointer:coarse)]:h-11";
const inactive = "font-medium text-brk-text hover:bg-brk-bg";
const active = "bg-brk-accent/10 font-semibold text-brk-accent";
/** A collapsed rail row: the icon alone, centred (lg+ only). */
const railRow = "lg:justify-center lg:gap-0 lg:px-0";

function navClass(collapsed: boolean) {
  return ({ isActive }: { isActive: boolean }): string =>
    `${linkBase} ${collapsed ? railRow : ""} ${isActive ? active : inactive}`;
}

/** The rail's own control (BRK-S244): a small round button that sits on the
 *  sidebar's right border, half in and half out (Nav, BRK-S258), level with
 *  the first destination, so it follows the edge as the rail folds. Desktop
 *  only. Its containing block is the sticky <aside>. Level means centred on
 *  the first row: under the top band, the list's 4px top padding plus the 8px
 *  that centres a 24px control on a 40px row, 12px in all. */
function RailToggle({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) {
  const label = collapsed ? "Expand sidebar" : "Collapse sidebar";
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-expanded={!collapsed}
      aria-controls={SIDEBAR_ID}
      onClick={onToggle}
      className="absolute -right-3 top-[calc(var(--brk-header-h)_+_0.75rem)] z-20 hidden h-6 w-6 items-center justify-center rounded-full border border-brk-border bg-brk-panel text-brk-dim shadow-sm transition-colors hover:bg-brk-bg hover:text-brk-text lg:inline-flex"
    >
      {collapsed ? (
        <ChevronsRight size={16} aria-hidden />
      ) : (
        <ChevronsLeft size={16} aria-hidden />
      )}
    </button>
  );
}

/** A row's label: its accessible name always, visible unless the rail is
 *  collapsed (where the row's title shows it on hover). */
function RowLabel({
  collapsed,
  children,
}: {
  collapsed: boolean;
  children: React.ReactNode;
}) {
  return (
    <span className={collapsed ? "lg:sr-only" : undefined}>{children}</span>
  );
}

// Each destination's outline icon, keyed as NavItemConfig names them.
const icons: Record<string, LucideIcon> = {
  exchange: LayoutGrid,
  directMarkets: Store,
  accounts: Building2,
  submissions: FileText,
  quotes: Tag,
  binders: Notebook,
  policies: ShieldCheck,
  declinations: Ban,
  // Surety platform icons.
  construction: HardHat,
  commercialBond: Landmark,
  bondLibrary: Library,
  bonds: ScrollText,
  obligees: Users,
  claims: ShieldAlert,
  documents: Folder,
  commissions: CircleDollarSign,
  support: Headset,
};

/** A row's icon: dim until its row is hovered, the accent on the current one. */
function NavIcon({ name, current }: { name: string; current: boolean }) {
  const Icon = icons[name] ?? LayoutGrid;
  return (
    <Icon
      size={22}
      strokeWidth={1.75}
      aria-hidden
      className={`shrink-0 ${
        current
          ? ""
          : "text-brk-dim transition-colors group-hover:text-brk-text"
      }`}
    />
  );
}

// Shared by both platforms, at the end of the list. Neither has a route yet,
// so each renders through the same InertNavItem as an unwired destination.
const SHARED: { label: string; icon: string }[] = [
  { label: "Documents", icon: "documents" },
  { label: "Commissions", icon: "commissions" },
];

/**
 * A nav row with no route — a destination not wired yet, or Support, which is
 * an action rather than a destination. They share the markup because they
 * share the behaviour.
 *
 * `onOpenChat` present ⇒ the row opens live chat, and it must NOT be
 * `aria-disabled`: announcing a control as unavailable while it opens a chat
 * window is worse than either behaviour on its own. Absent (chat unconfigured)
 * ⇒ the row is genuinely inert, and says so.
 */
function InertNavItem({
  label,
  icon,
  onOpenChat,
  collapsed,
  className = "",
}: {
  label: string;
  icon: string;
  onOpenChat?: () => void;
  collapsed: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-disabled={onOpenChat ? undefined : "true"}
      onClick={onOpenChat}
      title={collapsed ? label : undefined}
      className={`${linkBase} ${inactive} ${collapsed ? railRow : ""} w-full text-left ${className}`}
    >
      <NavIcon name={icon} current={false} />
      <RowLabel collapsed={collapsed}>{label}</RowLabel>
    </button>
  );
}

export function SideNav({
  identity,
  onNavigate,
  collapsed = false,
  onToggleCollapsed,
  platformSwitcher = false,
}: SideNavProps) {
  const { productLine } = useProductLine();
  const { enabled: chatEnabled, open: openChat } = useSupportChat();
  // Destinations for the active platform, with capability-gated items filtered
  // out (e.g. Accounts requires account:view).
  const items = PRODUCT_LINE_NAV[productLine].filter(
    (item) => !item.capability || hasCapability(identity, item.capability),
  );
  const navRef = useRef<HTMLElement>(null);
  const openChatIfEnabled = chatEnabled ? openChat : undefined;

  return (
    <div className="flex h-full flex-col text-brk-text">
      {/* The top block (the design's identity block, BRK-S258): the Signal
          logo, in a band as tall as the top bar so the two line up. Collapsed,
          the mark alone keeps the band. */}
      <div
        className={`flex h-[var(--brk-header-h)] shrink-0 items-center px-4 ${
          collapsed ? "lg:justify-center lg:px-0" : ""
        }`}
      >
        <div className={collapsed ? "lg:hidden" : undefined}>
          <Brand layout="logo" />
        </div>
        {collapsed && (
          <div className="hidden lg:block">
            <Brand layout="mark" />
          </div>
        )}
      </div>
      {onToggleCollapsed && (
        <RailToggle collapsed={collapsed} onToggle={onToggleCollapsed} />
      )}

      {platformSwitcher && (
        <div className="mx-4 mb-2 border-b border-brk-border pb-3 sm:hidden">
          <ProductLineSwitcher fill />
        </div>
      )}

      {/* The destinations scroll here when they don't fit (BRK-S229); the cue
          on the list's bottom edge says so (BRK-S241). It sits outside the
          nav, so it stays put while the list scrolls under it. Every row is
          one height, so the row count is what moves the list's end. */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        <nav
          ref={navRef}
          aria-label="Primary"
          className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-3 pb-3 pt-1"
        >
          {items.map((item) =>
            item.inert || !item.to ? (
              <InertNavItem
                key={item.label}
                label={item.label}
                icon={item.icon}
                onOpenChat={openChatIfEnabled}
                collapsed={collapsed}
              />
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                title={collapsed ? item.label : undefined}
                className={navClass(collapsed)}
                onClick={onNavigate}
              >
                {({ isActive }) => (
                  <>
                    <NavIcon name={item.icon} current={isActive} />
                    <RowLabel collapsed={collapsed}>{item.label}</RowLabel>
                  </>
                )}
              </NavLink>
            ),
          )}

          {SHARED.map((item) => (
            <InertNavItem
              key={item.label}
              label={item.label}
              icon={item.icon}
              onOpenChat={openChatIfEnabled}
              collapsed={collapsed}
            />
          ))}
        </nav>
        <ListScrollCue targetRef={navRef} watch={items.length} />
      </div>

      {/* The bottom group, under a divider (BRK-S258): Support, an action
          rather than a destination, so it stays put while the list scrolls.
          At lg+ it is as tall as the footer, so the divider meets the
          footer's top border, and its row is 32px so it clears the 40px band
          (BRK-S262). */}
      <div className="shrink-0 border-t border-brk-border px-3 py-3 lg:flex lg:h-[var(--brk-footer-h)] lg:flex-col lg:justify-center lg:py-0">
        <InertNavItem
          label="Support"
          icon="support"
          onOpenChat={openChatIfEnabled}
          collapsed={collapsed}
          className="lg:h-8"
        />
      </div>
    </div>
  );
}
